import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import TableContainer from "../../components/Common/TableContainer";
import swal from "sweetalert";
import { delVoyageData, getallvoyages } from "helpers/requests";

const VoyageOrganise = (props) => {
  useEffect(() => {
    document.title = "Voyage organisé";

    const fetchVoyages = async () => {
      const response = await getallvoyages();
      setHotels(response);
      setFilteredHotels(response); // Initialize filtered data
    };

    fetchVoyages();
  }, []);

  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]); // State for filtered data
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("vo_search") || "");
  const [availabilityFilter, setAvailabilityFilter] = useState(() => localStorage.getItem("vo_avail") || "all");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Persist filters
  useEffect(() => {
    localStorage.setItem("vo_search", searchTerm);
    localStorage.setItem("vo_avail", availabilityFilter);
  }, [searchTerm, availabilityFilter]);

  // Handle search and filter logic
  useEffect(() => {
    let updatedHotels = hotels;

    // Filter by availability
    if (availabilityFilter !== "all") {
      const isAvailable = availabilityFilter === "available";
      updatedHotels = updatedHotels.filter(
        (hotel) => hotel.availability === isAvailable
      );
    }

    // Filter by search term
    if (searchTerm) {
      updatedHotels = updatedHotels.filter((hotel) =>
        `${hotel.title} ${hotel.property?.name || ""} ${hotel.cite}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredHotels(updatedHotels);
  }, [searchTerm, availabilityFilter, hotels]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredHotels.map((hotel) => hotel.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleDelete = (hotelData) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Une fois supprimé, vous ne pourrez pas récupérer cette Voyage ",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async(willDelete) => {
      if (willDelete) {
        await delVoyageData(hotelData.id)

        const response = await getallvoyages();
        setHotels(response);
        setFilteredHotels(response); 

        swal("Voyage supprimée!", { icon: "success" });
      } else {
        swal("Suppression annulée");
      }
    });
  };

  const handleCheck = (hotelData) => {
    navigate(`/voyage-organise/${hotelData.id}`);
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Title",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "City",
        accessorKey: "cite",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Property",
        accessorKey: "property.name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => cellProps.row.original.property?.name || "—",
      },
      {
        header: "Availability",
        accessorKey: "availability",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <Badge
            color={cellProps.row.original.availability ? "success" : "danger"}
          >
            {cellProps.row.original.availability ? "Available" : "Unavailable"}
          </Badge>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <span
              className="text-primary"
              style={{ cursor: "pointer" }}
              id={`checktooltip-${cellProps.row.original.id}`}
              onClick={() => handleCheck(cellProps.row.original)}
            >
              <i className="mdi mdi-eye font-size-18" />
              <UncontrolledTooltip
                placement="top"
                target={`checktooltip-${cellProps.row.original.id}`}
              >
                Check
              </UncontrolledTooltip>
            </span>

            <span
              className="text-danger"
              style={{ cursor: "pointer" }}
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </span>


           
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked, filteredHotels]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Voyage organise")}
            breadcrumbItem={props.t("Voyage organise")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>
                    {props.t("Réservation de Voyage organise")}
                  </CardTitle>

                  {/* Filters */}
                  <Row className="mb-3">
                  <Col md="4">
                      <FormGroup>
                        <Label for="availabilitySelect">Filter by Availability</Label>
                        <Input
                          type="select"
                          id="availabilitySelect"
                          value={availabilityFilter}
                          onChange={(e) =>
                            setAvailabilityFilter(e.target.value)
                          }
                        >
                          <option value="all">All</option>
                          <option value="available">Available</option>
                          <option value="unavailable">Unavailable</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="searchInput">Search</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Search by title, property, or city"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="4" className="d-flex align-items-end justify-content-center mb-2">
                    <Button
                      color="success"
                      className="btn-rounded waves-effect waves-light mb-2"
                      onClick={() => { window.location.href = 'voyage-organise/add' }}
                    >
                      Ajoute un voyage organize 
                    </Button>
                  </Col>
                  </Row>

                  <TableContainer
                    columns={columns}
                    data={filteredHotels}
                    isGlobalFilter={false}
                    isAddButton={false}
                    isPagination={true}
                    SearchPlaceholder="Cherchez un Voyage"
                    buttonClass="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2 addOrder-modal"
                    buttonName="Ajoute un voyage organisé"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

VoyageOrganise.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(VoyageOrganise);
